import axios, { type AxiosPromise, type AxiosRequestConfig, type AxiosResponse, AxiosError } from 'axios';

import {
  type ServiceError,
  type ServiceResult,
  type ServiceSuccess,
  isServiceSuccess,
} from '@core/models/actionResponse';

import type { identifier } from '@core/models/base';
import { router } from '../routing';
import { getAdminTabSessionId } from './adminSessionTabId';
import { authenticationHandler } from './authenticationHandler';
import type {
  ImageLookupDisplayModel,
  BulkImageUsageResponse,
  BulkReplaceResponse,
} from './models/images/imageLookUps';
import type { SubjectCreateModel, SubjectDisplayModel } from './models/subjects/subjects';
import type { WorkflowDisplayModel, WorkflowCreateModel, WorkflowUpdateModel } from './models/workflows/workflows';
import { startRequest, completeRequest } from './composables/useDebugRequestHistory';

// Extend axios config to include debug tracking ID.
// Note: InternalAxiosRequestConfig does not need to be imported here — the
// `declare module 'axios'` block augments the interface declared in the axios
// package directly, so TypeScript resolves _debugRequestId on config objects
// without an explicit import of the type.
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _debugRequestId?: string;
    // Guards the authedAxios 401 interceptor against retry loops: a request is retried at most once
    // after a forced token refresh.
    _retry401?: boolean;
  }
}

/**
 * URL path segments whose request/response bodies should NOT be captured in debug history
 * to prevent plaintext credentials and tokens from appearing in exports.
 * Matching requires the pattern to begin at the start of the path or after a '/',
 * and to end at the end of the path or before a '/' or '?', preventing false positives
 * (e.g. '/api/token' won't match '/api/token-types').
 */
const SENSITIVE_URL_PATTERNS = [
  '/api/auth/',
  '/api/login',
  '/api/register',
  '/api/account/password',
  '/api/credentials',
  '/api/token',
  '/api/keys',
  '/api/secrets',
];

function isSensitiveUrl(url: string): boolean {
  // Strip query string for matching
  const path = url.toLowerCase().split('?')[0];
  return SENSITIVE_URL_PATTERNS.some((pattern) => {
    // Normalise pattern: strip any trailing slash for uniform boundary checks.
    // Since all patterns start with '/', the leading '/' already acts as a natural
    // segment boundary on the left side, so we only need a right-boundary check to
    // prevent partial matches like '/api/token-types' matching '/api/token'.
    const p = pattern.endsWith('/') ? pattern.slice(0, -1) : pattern;
    const idx = path.indexOf(p);
    if (idx === -1) return false;
    // Must end at path end, or before '/' (next segment) or '?' (query string)
    const charAfter = path[idx + p.length];
    return charAfter === undefined || charAfter === '/' || charAfter === '?';
  });
}

// Debug request tracking interceptors for base axios instance
axios.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    const body = isSensitiveUrl(url) ? null : config.data;
    const id = startRequest(config.method || 'GET', url, body);
    config._debugRequestId = id;
    return config;
  },
  (error) => Promise.reject(error),
);

axios.interceptors.response.use(
  (response) => {
    const id = response.config._debugRequestId;
    if (id) {
      const url = response.config.url || '';
      completeRequest(id, response.status, isSensitiveUrl(url) ? null : response.data);
    }
    return response;
  },
  (error) => {
    if (error instanceof AxiosError) {
      const id = error.config?._debugRequestId;
      if (id) {
        const url = error.config?.url || '';
        completeRequest(
          id,
          error.response?.status ?? null,
          isSensitiveUrl(url) ? null : error.response?.data,
          error.message,
        );
      }
      if (error.response?.status === 503) {
        router.push('/maintenance');
      }
    }

    return Promise.reject(error);
  },
);

export const authedAxios = axios.create({});

// Debug request tracking for authedAxios
authedAxios.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    const body = isSensitiveUrl(url) ? null : config.data;
    const id = startRequest(config.method || 'GET', url, body);
    config._debugRequestId = id;
    return config;
  },
  (error) => Promise.reject(error),
);

// Auth token injection for authedAxios
authedAxios.interceptors.request.use(
  async (config) => {
    const token = await authenticationHandler.getToken();
    if (config && isServiceSuccess(token)) {
      config.headers['Authorization'] = `Bearer ${token.data.token}`;
    }
    // Identifies this tab to the single-admin-session guard (singleAdminSession flag).
    // Harmless everywhere else: the server ignores it outside gated studio-admin routes.
    config.headers['X-Admin-Session-Id'] = getAdminTabSessionId();
    return config;
  },
  (err) => Promise.reject(err),
);

// Debug response tracking for authedAxios
authedAxios.interceptors.response.use(
  (response) => {
    const id = response.config._debugRequestId;
    if (id) {
      const url = response.config.url || '';
      completeRequest(id, response.status, isSensitiveUrl(url) ? null : response.data);
    }
    return response;
  },
  async (error) => {
    if (error instanceof AxiosError) {
      const id = error.config?._debugRequestId;
      if (id) {
        const url = error.config?.url || '';
        completeRequest(
          id,
          error.response?.status ?? null,
          isSensitiveUrl(url) ? null : error.response?.data,
          error.message,
        );
      }

      // Another tab holds this user's studio-admin lease (singleAdminSession flag).
      // Route to the admin-session store, which shows the blocked/kicked modal; the
      // request itself still rejects so callers don't mistake it for success.
      // Dynamic import: axios.ts loads before Pinia is installed, and a static store
      // import would create a module cycle through the service layer.
      if (error.response?.status === 409 && (error.response.data as { code?: string })?.code === 'adminSessionConflict') {
        // Only gated studio-admin endpoints 409 with this code, so the URL always carries
        // the studio id; the store needs it in case the conflict beats the router guard.
        // No trailing slash in the pattern: some URLs end at the id (GET .../studios/29).
        const studioIdMatch = /\/api\/studioadmin\/studios\/(\d+)/.exec(error.config?.url ?? '');
        import('./stores/adminSession.store')
          .then(({ useAdminSessionStore }) =>
            useAdminSessionStore().onConflict(studioIdMatch ? Number(studioIdMatch[1]) : undefined),
          )
          .catch(() => {
            /* modal is best-effort; the rejected request already stops the caller */
          });
      }

      // Recover from an expired/invalidated session instead of silently failing the write.
      // Previously a 401 became a swallowed ServiceError, so a session that expired mid-edit made
      // every save look like a no-op with no re-login prompt (the demo's "changes don't take" bug).
      // Force one token refresh and retry the original request; if the refresh fails,
      // loginWithRefreshToken handles the redirect to login for an invalid refresh token.
      if (error.response?.status === 401 && error.config && !error.config._retry401) {
        error.config._retry401 = true;
        const refreshResult = await authenticationHandler.forceRefresh();
        if (isServiceSuccess(refreshResult)) {
          error.config.headers['Authorization'] = `Bearer ${refreshResult.data.token}`;
          return authedAxios(error.config);
        }
      }
    }
    return Promise.reject(error);
  },
);

export const getAuthorizationHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? `Bearer ${token}` : null;
};

export const uploadFile = async <Tout>(
  url: string,
  file: File,
  method: 'PUT' | 'POST' | 'PATCH',
  fileFormName = 'File',
) => {
  const data = new FormData();
  data.append(fileFormName, file);
  return authedRequest<Tout>({
    method,
    url,
    data,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const uploadForm = async <Tout>(url: string, data: FormData, method: 'PUT' | 'POST' | 'PATCH') => {
  return authedRequest<Tout>({
    method,
    url,
    data,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const get = async <Tout>(url: string, params?: any) => {
  return await authedRequest<Tout>({
    url,
    params,
    method: 'get',
  });
};

export const post = async <Tout>(url: string, data: any) => {
  return await authedRequest<Tout>({
    url,
    data,
    method: 'post',
  });
};

export const patch = async <Tout>(url: string, data?: any) => {
  return await authedRequest<Tout>({
    url,
    data,
    method: 'patch',
  });
};

export const put = async <Tout>(url: string, data?: any) => {
  return await authedRequest<Tout>({
    url,
    data,
    method: 'put',
  });
};

export const deleteReq = async <Tout>(url: string) => {
  return await authedRequest<Tout>({ url, method: 'delete' });
};

export const axiosToServiceResult = <Tout>(axiosReq: AxiosPromise<Tout>): Promise<ServiceResult<Tout>> => {
  return new Promise((resolve) => {
    axiosReq
      .then((response) => {
        const data = (response as AxiosResponse<Tout>).data;
        resolve({ data } as ServiceSuccess<Tout>);
      })
      .catch((error) => {
        console.log('Request error', error);
        resolve({ errors: error?.response?.data || [error] } as ServiceError);
      });
  });
};

export const authedRequest = <Tout>(config: AxiosRequestConfig<any>): Promise<ServiceResult<Tout>> => {
  return axiosToServiceResult(authedAxios(config));
};

export const apiClient = {
  v1: {
    imageLookups: {
      replaceImage: (
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        imageLookupId: identifier,
        file: File,
      ): Promise<ServiceResult<ImageLookupDisplayModel>> => {
        const formData = new FormData();
        formData.append('file', file);

        return uploadForm(
          `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/imagelookups/${imageLookupId}`,
          formData,
          'PATCH',
        );
      },

      deleteImage: (
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        imageLookupId: identifier,
      ): Promise<ServiceResult<identifier>> => {
        return deleteReq(
          `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/imagelookups/${imageLookupId}`,
        );
      },

      convertImageType: (
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        imageLookupId: identifier,
      ): Promise<ServiceResult<ImageLookupDisplayModel>> => {
        return patch(
          `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/imagelookups/${imageLookupId}/convert-type`,
        );
      },

      generateThumbnail: (
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        imageLookupId: identifier,
      ): Promise<ServiceResult<ImageLookupDisplayModel>> => {
        return post(
          `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/imagelookups/${imageLookupId}/generate-thumbnail`,
          {},
        );
      },

      checkBulkImageUsage: (
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        imageIds: number[],
      ): Promise<ServiceResult<BulkImageUsageResponse>> => {
        return post(
          `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/imagelookups/check-usage`,
          { ImageIds: imageIds },
        );
      },

      bulkReplaceImages: (
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        files: File[],
      ): Promise<ServiceResult<BulkReplaceResponse>> => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append('files', file);
        });

        return uploadForm(
          `/api/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/imagelookups/bulk-replace`,
          formData,
          'PATCH',
        );
      },
    },
  },

  v3: {
    subjects: {
      async create(
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        createModel: SubjectCreateModel,
      ) {
        return await post<SubjectDisplayModel>(
          `/api/v3/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/subjects`,
          createModel,
        );
      },

      async createBulk(
        studioId: identifier,
        organizationId: identifier,
        jobId: identifier,
        createModels: SubjectCreateModel[],
        returnData?: boolean,
      ) {
        const url = returnData
          ? `/api/v3/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/subjects/bulk?returnData=true`
          : `/api/v3/studioadmin/studios/${studioId}/organizations/${organizationId}/jobs/${jobId}/subjects/bulk`;

        return await post<SubjectDisplayModel[]>(url, createModels);
      },
    },

    workflows: {
      async getWorkflows() {
        const result = await get<WorkflowDisplayModel[]>('/api/v3/workflows');
        if (isServiceSuccess(result)) {
          return result.data;
        } else {
          throw new Error('Failed to fetch workflows');
        }
      },
    },
  },
};

export const adminApiClient = {
  workflows: {
    async getWorkflows() {
      const result = await get<WorkflowDisplayModel[]>('/api/admin/workflows');
      if (isServiceSuccess(result)) {
        return result.data;
      } else {
        throw new Error('Failed to fetch workflows');
      }
    },

    async createWorkflow(workflow: WorkflowCreateModel) {
      return await post<WorkflowDisplayModel>('/api/admin/workflows', workflow);
    },

    async updateWorkflow(workflow: WorkflowUpdateModel) {
      return await put<WorkflowDisplayModel>(`/api/admin/workflows/${workflow.id}`, workflow);
    },

    async deleteWorkflow(id: number) {
      const result = await deleteReq<void>(`/api/admin/workflows/${id}`);
      if (!isServiceSuccess(result)) {
        throw new Error(`Failed to delete workflow with ID ${id}`);
      }
    },
  },
};
